import { Services } from "@ca.vmedia.tv/services";
import { VodContentType, OnDemandContent, Genre, OnDemandData, type OnDemandItem } from "@ca.vmedia.tv/domains";
import { AppConstants } from "@/common";
import { useApplicationStore } from "@/stores/Application";
import { OnDemandCardData, TabsGenresCardData, type GenreCardData, type ShowAllCardData, type CardData } from "@/components/Cards/types";
import { useExtraCards } from "@/composables/useExtraCards";
import { Logger } from '@/utils';
import { useGenresStore } from "@/stores/Genres";
import { useVodPageStore } from "@/stores/VodPage";
import { useEmptyLine } from "@/composables/useEmptyLine";
import type { ExposeInterface } from './types';
import type { AxiosError } from "axios";

export const useLinesVodGenresStore = defineStore('LinesVodGenres', (): ExposeInterface => {
    const { 
        emptyOnDemandContent,
        emptyOnDemandData
    } = useEmptyLine();

    const { getSubGenres, getGenre, getSubChannels, setGenreContent } = useGenresStore();

    const { hasShowAll, getCardShowAll } = useExtraCards();

    const ApplicationStore = useApplicationStore();
    const { getFilterVodParams } = storeToRefs(ApplicationStore);
    const { fetchCurrentGenre } = useVodPageStore();

    const _currentGenre: Ref<Genre | null> = ref(null);
    const getCurrentGenre = computed((): Genre | null => _currentGenre.value);
    const setCurrentGenre = (id: number): Genre | null => {
        _currentGenre.value = getGenre(id);
        fetchCurrentGenre(id);
        return _currentGenre.value;
    };

    const fetchSubGenres = (): void => {
        if (!getCurrentGenre.value?.id) {
            throw new Error("Genre id is not defined in fetchSubGenres");
        }
        const vodSubGenres = getSubGenres(getCurrentGenre.value.id);
        const genreCache = vodSubGenres.find((element: Genre)=> (element.parentId == getCurrentGenre.value?.id && element.content != null) ? true :  false);
        
        const {showType} = getFilterVodParams.value;
        if (genreCache && genreCache.content && !showType) {
            return;
        }

        vodSubGenres.forEach((subGenre: Genre) => {
            setSubGenreContent(subGenre);
        });
    };

    const setSubGenreContent = (genre: Genre) => {
        const {showType, cashDisabled} = getFilterVodParams.value;
        const updatedGenre = new Genre(genre.id);

        Services.Instance.vod.getVodContent(
            VodContentType.GenreSubitems,
            AppConstants.GenreItemsInLine,
            1,
            genre,
            showType,
            cashDisabled
        ).then((data) => {
            if (data.items) {
                updatedGenre.content = new OnDemandContent(data.items, null, null, null, data.paging);
            } else {
                updatedGenre.content = emptyOnDemandContent();
            }
        }).catch((error) => {
            updatedGenre.content = emptyOnDemandContent();
            Logger.captureError(error);
        }).finally(() => {
            setGenreContent(updatedGenre);
        });
    };
    const _newShowsGenres: Ref<OnDemandData | null> = ref(null);
    const fetchNewShowsGenres = async (): Promise<void> => {
        try {
            if (!getCurrentGenre.value) {
                throw new Error("Genre is null in fetchNewShowsGenres");
            }
            if (_newShowsGenres.value !== null) {
                return;
            }

            _newShowsGenres.value = await Services.Instance.vod.getVodContent(
                VodContentType.NewSeries,
                AppConstants.ItemsInLine,
                1,
                getCurrentGenre.value,
            );
        } catch (error) {
            _newShowsGenres.value = emptyOnDemandData();
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const getCardsNewShowsGenres = computed((): (OnDemandCardData | ShowAllCardData)[] | null => {
        const data = _newShowsGenres.value;

        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = data.items.map((item) => new OnDemandCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    });

    const _newMoviesGenres: Ref<OnDemandData | null> = ref(null);
    const fetchNewMoviesGenres = async (): Promise<void> => {
        try {
            if (!getCurrentGenre.value) {
                throw new Error("Genre is null in fetchNewMoviesGenres");
            }
            if (_newMoviesGenres.value !== null) {
                return;
            }

            const {showType} = getFilterVodParams.value;

            _newMoviesGenres.value = await Services.Instance.vod.getVodContent(
                VodContentType.NewMovies,
                AppConstants.ItemsInLine,
                1,
                getCurrentGenre.value,
                showType
            );
        } catch (error) {
            _newMoviesGenres.value = emptyOnDemandData();
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const getCardsNewMoviesGenres = computed((): (OnDemandCardData | ShowAllCardData)[] | null => {
        const data = _newMoviesGenres.value;

        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = data.items.map((item) => new OnDemandCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    }); 

    const _moviesGenres: Ref<OnDemandData | null> = ref(null);
    const fetchMoviesGenres = async (): Promise<void> => {
        try {
            if (!getCurrentGenre.value) {
                throw new Error("Genre is null in fetchMoviesGenres");
            }
            if (_moviesGenres.value !== null) {
                return;
            }

            _moviesGenres.value = await Services.Instance.vod.getVodContent(
                VodContentType.AllMovies,
                AppConstants.ItemsInLine,
                1,
                getCurrentGenre.value,
            );
        } catch (error) {
            _moviesGenres.value = emptyOnDemandData();
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const getCardsMoviesGenres = computed((): (OnDemandCardData | ShowAllCardData)[] | null => {
        const data = _moviesGenres.value;

        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = data.items.map((item) => new OnDemandCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    });

    const _showAllItems: Ref<OnDemandData | null> = ref(null);
    const getShowAllMoviesCards: ComputedRef<CardData[] | null> = computed(() => {
        if (_showAllItems.value === null || _showAllItems.value.items === null) {
            return null;
        }
        
        return _showAllItems.value.items.map((item) => new OnDemandCardData(item));
    });
    const setShowAllMovies = (): void => {
        try {
            if (_showAllItems.value !== null) {
                return;
            };
            _showAllItems.value = new OnDemandData(_moviesGenres.value?.items, null, _moviesGenres.value?.paging);
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const _isLoadingShowAll: Ref<boolean> = ref(false);
    const getIsLoadingShowAll: ComputedRef<boolean> = computed(() => _isLoadingShowAll.value);
    const _setIsLoadingShowAll = (isLoading: boolean) => {
        _isLoadingShowAll.value = isLoading;
    };
    const loadShowAllMoviesNextPage = async () => {
        if( _isLoadingShowAll.value === true){
            return;
        }
        const pageNumber = !_showAllItems.value?.items || !_showAllItems.value?.paging?.itemsOnPage
        ? -1
        : Math.ceil(_showAllItems.value.items.length / _showAllItems.value.paging.itemsOnPage) + 1;
        if(pageNumber < 0 || !_showAllItems.value?.paging?.totalPages || pageNumber > _showAllItems.value.paging?.totalPages){
            return;
        }

        try {
            if (!getCurrentGenre.value) {
                throw new Error("Genre is null in loadShowAllMoviesNextPage");
            }
            _setIsLoadingShowAll(true);

            const data = await Services.Instance.vod.getVodContent(
                VodContentType.AllMovies,
                AppConstants.ItemsInLine,
                pageNumber,
                getCurrentGenre.value,
            );
    
            if (data && data.items !== null && data.items.length != 0) {
                const items = (_showAllItems.value.items as OnDemandItem[]).concat(data.items);
                _showAllItems.value = new OnDemandData(items, null, _showAllItems.value.paging);
            }
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        } finally {
            _setIsLoadingShowAll(false);
        }
    };

    const getCardsGenre = (genre: Genre): (OnDemandCardData | ShowAllCardData)[] | null => {
        if (genre.content === null || genre.content.items === null) {
            return null;
        }
        
        const items = genre.content.items.map((item) => new OnDemandCardData(item));

        if (hasShowAll(genre.content.paging)) {
            return [...items, ...getCardShowAll(items, genre.content.paging)];
        }
        return items;
    };

    const getCardsSubChannels = (genre: Genre | null): TabsGenresCardData[] => {
        if (genre === null || genre.id === null) {
            return [];
        }
        const subChannels = getSubChannels(genre.id); 
        const items: TabsGenresCardData[] = [];

        subChannels.forEach((item: GenreCardData, index: number) => {
            const contentItems = new TabsGenresCardData("tabs-genres-card", item.getName(), index, item.getId(), item.getIsPremium());
            items.push(contentItems);
        });
        
        return items;
    };


    const _counterRows: Ref<number> = ref(AppConstants.VodCounterLines);
    const getCounterLinesVodGenres = computed((): number => _counterRows.value);
    const increaseCounterLinesVodGenres = (): void => {
        _counterRows.value += 1;
    };

    const clearAllLinesVodGenresStore =  (): void => {
        _counterRows.value = AppConstants.VodCounterLines;
        _newMoviesGenres.value = null;
        _newShowsGenres.value = null;
        _moviesGenres.value = null;
        _showAllItems.value = null;
        _currentGenre.value = null;
    };


    return {
        fetchNewShowsGenres,
        fetchNewMoviesGenres,
        fetchMoviesGenres,
        fetchSubGenres,
        getCardsNewShowsGenres,
        getCardsNewMoviesGenres,
        getCardsMoviesGenres,
        getCounterLinesVodGenres,
        getCardsGenre,
        getCardsSubChannels,
        setCurrentGenre,
        clearAllLinesVodGenresStore,
        increaseCounterLinesVodGenres,
        getCurrentGenre,
        getIsLoadingShowAll,
        loadShowAllMoviesNextPage,
        setShowAllMovies,
        getShowAllMoviesCards
    };
});